import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { IconX, IconEye, IconCheck, IconSearch } from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import CustomTable from '@/components/Table/index';
import {
  SupportStatusConfig,
  SUPPORT_STATUS_OPTIONS,
} from '@/constants/constant';
import { ESupportStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getSupportQueries,
  updateSupportQueryStatus,
} from '@/service/supportQueryService';
import { ISupportQuery } from '@/types';
import { ISupportUpdatePayload } from '@/types/payload';
import { formatUnixToLongDate } from '@/utils';

import DiscardModal from './modals/DiscardModal';

const SupportQueries = () => {
  const { formatText } = useFormatText();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ISupportQuery[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  const [status, setStatus] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const pageTitle = formatText('support.title');
  const isEmpty = !loading && data.length === 0 && !debouncedSearch && !status;
  const isEmptySearch =
    !loading && data.length === 0 && (!!debouncedSearch || !!status);

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        order: sortField ? { [sortField]: sortDirection } : undefined,
        status: status || undefined,
      };
      const response = await getSupportQueries(params);
      if (response?.status) {
        setData(response?.data || []);
        setTotal(response?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching support queries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    status,
  ]);

  const updateStatus = async (id: string, payload: ISupportUpdatePayload) => {
    setActionLoading(true);

    try {
      const response = await updateSupportQueryStatus(id, payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, {
          variant: 'success',
        });

        setIsDiscardModalOpen(false);
        fetchData();
      } else {
        console.error('Error response:', response?.message);
      }
    } catch (error) {
      console.error(`Error updating query`, error);
    } finally {
      setActionLoading(false);
      setSelectedId?.(null);
    }
  };

  const handleResolve = (id: string) =>
    updateStatus(id, { status: ESupportStatus.RESOLVED });

  const handleDiscard = (reason: string) => {
    if (selectedId) {
      updateStatus(selectedId, {
        status: ESupportStatus.DISCARD,
        discard_reason: reason,
      });
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'support.columns.report-id',
      key: 'report_id',
      sorter: true,
      handleSort: () => handleSort('report_id'),
      render: (row: ISupportQuery) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.report_id}
        </span>
      ),
    },
    {
      title: 'support.columns.reported-by',
      key: 'reported_by',
      sorter: true,
      handleSort: () => handleSort('reported_by'),
      render: (row: ISupportQuery) => (
        <span className="text-sm">{row?.reported_by ?? '-'}</span>
      ),
    },
    {
      title: 'support.columns.issue',
      key: 'issue',
      sorter: true,
      handleSort: () => handleSort('issue'),
      render: (row: ISupportQuery) => (
        <span className="text-sm truncate max-w-62.5 block" title={row?.issue}>
          {row?.issue}
        </span>
      ),
    },
    {
      title: 'support.columns.status',
      key: 'status',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: ISupportQuery) => {
        const config = SupportStatusConfig[row?.status as ESupportStatus];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label || row?.status)}
          </span>
        );
      },
    },
    {
      title: 'support.columns.date',
      key: 'created_at',
      sorter: true,
      handleSort: () => handleSort('created_at'),
      render: (row: ISupportQuery) => (
        <span className="text-sm">
          {formatUnixToLongDate(Number(row?.created_at))}
        </span>
      ),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      align: 'center',
      render: (row: ISupportQuery) => {
        const isDisabled =
          row?.status === ESupportStatus.RESOLVED ||
          row?.status === ESupportStatus.DISCARD;

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () =>
              navigate(`/support-queries/${row.id}`, {
                state: { reportId: row?.report_id },
              }),
          },
          {
            key: 'resolve',
            label: 'support.actions.resolve',
            icon: <IconCheck size={18} />,
            disabled: isDisabled,
            onClick: () => handleResolve(row.id),
          },
          {
            key: 'discard',
            label: 'support.actions.discard',
            icon: <IconX size={18} />,
            isDanger: true,
            disabled: isDisabled,
            onClick: () => {
              setSelectedId(row.id);
              setIsDiscardModalOpen(true);
            },
          },
        ];
        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6 ps-1">
      <div className="flex items-center gap-2.5">
        <Input
          name="search"
          placeholder={formatText('support.search-placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={{
            icon: <IconSearch size={20} className="text-neutral-500" />,
          }}
          className="max-w-70"
          inputClassName="py-[13px]"
        />
        <Select
          placeholder={formatText('support.columns.status')}
          options={SUPPORT_STATUS_OPTIONS}
          selected={status}
          onChange={(value) => setStatus(value as string)}
          className="w-37.5! py-3.25"
          isTextFormat
          isClearable
        />
      </div>

      <CustomTable
        columns={columns}
        dataSource={data}
        isLoading={loading}
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        fieldName={sortField}
        sortDirection={sortDirection}
        isEmpty={isEmpty}
        emptyImgSrc="/assets/svg/no-support-query.svg"
        emptyTitle={formatText('support.empty-title')}
        emptyDescription={formatText('support.empty-desc')}
        isEmptySearch={isEmptySearch}
        onRowClick={(id) => {
          const row = data?.find((item) => item?.id === id);
          if (row)
            navigate(`/support-queries/${row.id}`, {
              state: { reportId: row?.report_id },
            });
        }}
      />

      {isDiscardModalOpen && (
        <DiscardModal
          setIsOpen={setIsDiscardModalOpen}
          onDiscard={handleDiscard}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
};

export default SupportQueries;
